/**
 * Client side of `GET /projects/:project/environments` of `waadoo-projects`
 * (backend `plugins/waadooProjects/permanentEnvironments.ts`): the permanent
 * environments a project's appenv repository declares, and which of them are
 * deployed — what the screens need to show the ones « en sommeil » (step 3
 * of the plan « branches protégées en sommeil »: shown, not activated).
 *
 * The types are re-declared rather than imported from the backend, as in
 * `pipelines.ts`: they are the contract of the route and change on both
 * sides at once.
 *
 * FAIL SOFT, EVERYWHERE: the route is registered only where
 * `waadooProjects.gitlab.{host,appenvsGroup}` are set (ludo), refuses with a
 * 404, and GitLab may be down. Every failure reads as « no permanent
 * environment listed », so no branch is shown asleep and every screen stays
 * as it was before this route existed.
 *
 * ONE READ PER PROJECT for every screen, shared for
 * {@link PERMANENT_ENVIRONMENTS_TTL_MS} (`sharedLoad.ts`), per `FetchApi`
 * instance as in `branchManagement.ts`: the project page, the branch page,
 * the sidebar and the home page ask for the same project.
 */

import { useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import { compareBranches } from './branchKind';
import {
  createSharedLoad,
  type SharedLoad,
  type SharedLoadOptions,
} from './sharedLoad';

/** The backend plugin serving the route. */
const PLUGIN_ID = 'waadoo-projects';

/**
 * How long one answer is shared: the backend's own cache of the GitLab read
 * behind it (`ENVIRONMENTS_READ_TTL_MS`).
 */
export const PERMANENT_ENVIRONMENTS_TTL_MS = 30_000;

/** Mirrored from the backend's `PermanentEnvironment`. */
export interface PermanentEnvironment {
  /** The permanent branch's name. */
  name: string;
  /** The appenv folder holds `.active`: the environment is deployed. */
  active: boolean;
  /**
   * The folder's `xovhs3bucket-media.yaml`, read: the media are on an S3
   * bucket of the environment's own (Wagtail with its media on S3);
   * `retained`, its `spec.retainOnDelete` — only a retained bucket outlives
   * the sleep, kept by a wake, emptied by a fresh one. Absent without the
   * file, or when the backend could not read it.
   */
  mediaBucket?: { retained: boolean };
}

/** Mirrored from the backend's `PermanentEnvironmentsResponse`. */
export interface PermanentEnvironmentsResponse {
  environments: PermanentEnvironment[];
}

interface Apis {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}

/** Rejects on any non-2xx answer; the callers read that as « none ». */
export async function fetchPermanentEnvironments(
  apis: Apis & { project: string },
): Promise<PermanentEnvironment[]> {
  // Absolute backend URL: in development the frontend runs on another port.
  const base = await apis.discoveryApi.getBaseUrl(PLUGIN_ID);
  const response = await apis.fetchApi.fetch(
    `${base}/projects/${encodeURIComponent(apis.project)}/environments`,
  );
  if (!response.ok) {
    throw new Error(`Permanent environments API: HTTP ${response.status}`);
  }
  const body = (await response.json()) as PermanentEnvironmentsResponse;
  return body.environments;
}

/** Never pruned: one entry per project read in the session, gone on reload. */
const permanentLoads = new WeakMap<
  FetchApi,
  Map<string, SharedLoad<PermanentEnvironment[]>>
>();

/**
 * The shared read of `project`'s permanent environments. Rejects when the
 * request fails; the failure is not kept.
 */
export function loadProjectPermanentEnvironments(
  apis: Apis & { project: string },
  options?: SharedLoadOptions,
): Promise<PermanentEnvironment[]> {
  let byProject = permanentLoads.get(apis.fetchApi);
  if (!byProject) {
    byProject = new Map();
    permanentLoads.set(apis.fetchApi, byProject);
  }
  let shared = byProject.get(apis.project);
  if (!shared) {
    shared = createSharedLoad<PermanentEnvironment[]>(
      PERMANENT_ENVIRONMENTS_TTL_MS,
    );
    byProject.set(apis.project, shared);
  }
  return shared.load(() => fetchPermanentEnvironments(apis), options);
}

/**
 * The branches shown « en sommeil »: the permanent environments the appenv
 * repository lists as NOT deployed (`active: false`) and for which ArgoCD
 * shows no Application (`deployedBranches`), in the order of every branch
 * list (`compareBranches`).
 *
 * An environment listed `active: true` without an Application yet is not
 * asleep: it is being created (or woken up), and shows nothing until its
 * Application appears. One listed `active: false` that still has an
 * Application is shown by that Application, as any deployed one — the
 * Application is what is running.
 */
export function sleepingBranches(
  permanent: readonly PermanentEnvironment[],
  deployedBranches: Iterable<string>,
): string[] {
  const deployed = new Set(deployedBranches);
  return permanent
    .filter(env => !env.active && !deployed.has(env.name))
    .map(env => env.name)
    .sort(compareBranches);
}

/**
 * The permanent environments of `project`, read once per project and shared
 * (above); undefined while asked and for no project, EMPTY on any failure
 * (fail soft, header) — so that a screen can wait for the answer
 * (undefined) and still fall back to its usual state when there is none.
 * `refreshKey`: a change reads again past the shared answer (Refresh).
 */
export function usePermanentEnvironments(
  project: string | undefined,
  refreshKey?: number,
): PermanentEnvironment[] | undefined {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  // Kept with the project it answers for: a page moving to another project
  // must not show the previous one's environments meanwhile.
  const [answer, setAnswer] = useState<{
    project: string;
    environments: PermanentEnvironment[];
  }>();
  // The key of the last read: a different one is a Refresh, read past the
  // shared answer.
  const lastRefresh = useRef(refreshKey);

  useEffect(() => {
    if (!project) {
      return undefined;
    }
    let active = true;
    const force = refreshKey !== lastRefresh.current;
    lastRefresh.current = refreshKey;
    const load = async () => {
      try {
        const environments = await loadProjectPermanentEnvironments(
          { fetchApi, discoveryApi, project },
          { force },
        );
        if (active) {
          setAnswer({ project, environments });
        }
      } catch {
        // Fail soft (header): an empty list, nothing is shown asleep.
        if (active) {
          setAnswer({ project, environments: [] });
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [project, fetchApi, discoveryApi, refreshKey]);

  return answer && answer.project === project ? answer.environments : undefined;
}
